/**
 * GuidedTour — Full-app onboarding tour using driver.js
 *
 * Shows once per user (first time after login). Once completed or
 * dismissed mid-way, it is permanently dismissed (backend-persisted).
 *
 * Steps are role-aware: parent sees Paie/Abonnement steps,
 * nanny sees Rejoindre step.
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Build the steps array based on user role AND viewport.
 *
 * On mobile (< 768 px) the nav is a **bottom bar** with different
 * data-testid selectors and the popover should open **above** the
 * element (`side: 'top'`).  On desktop the nav is a **top bar**.
 */
const buildSteps = (role, isMobile) => {
  const isParent = role === 'parent';

  const steps = [
    // ── Step 1: Welcome (no element — centered) ──
    {
      popover: {
        title: 'Bienvenue sur Nannyou ! 👋',
        description: isParent
          ? 'Nannyou simplifie la gestion de votre assistante maternelle : contrats, pointage, paie, et déclarations Pajemploi. Suivez cette visite pour découvrir les fonctionnalités.'
          : 'Nannyou vous aide à gérer votre temps de travail, vos contrats et vos congés. Suivez cette visite pour découvrir les fonctionnalités.',
      },
    },
    // ── Step 2: Dashboard ──
    {
      element: '[data-testid="dashboard-page"]',
      popover: {
        title: 'Tableau de bord',
        description: 'Votre vue d\'ensemble mensuelle : heures travaillées, nombre de contrats, salaire brut/net estimé, et activité récente.',
        side: 'bottom',
        align: 'center',
      },
    },
  ];

  if (isMobile) {
    // ── Mobile: bottom nav targets ──
    steps.push(
      {
        element: '[data-testid="nav-heures"]',
        popover: {
          title: 'Heures & Pointage',
          description: isParent
            ? 'Consultez et validez les heures de votre assistante maternelle. Les absences apparaissent en couleur distincte.'
            : 'Enregistrez vos heures d\'arrivée/départ et déclarez vos absences.',
          side: 'top',
        },
      },
      {
        element: '[data-testid="nav-contrats"]',
        popover: {
          title: 'Contrats',
          description: isParent
            ? 'Créez et gérez vos contrats. Invitez votre nounou par email ou code.'
            : 'Retrouvez tous vos contrats en cours et téléchargez le PDF.',
          side: 'top',
        },
      },
    );

    if (isParent) {
      steps.push(
        {
          element: '[data-testid="nav-paie"]',
          popover: {
            title: 'Paie & Pajemploi',
            description: 'Bulletins de paie mensuels, cotisations sociales officielles, et déclaration directe à Pajemploi/URSSAF.',
            side: 'top',
          },
        },
        {
          element: '[data-testid="nav-mobile-profile-trigger"]',
          popover: {
            title: 'Plus — Profil & Paramètres',
            description: 'Accédez à votre profil, abonnement Premium, cotisations, congés, et RGPD.',
            side: 'top',
          },
        },
      );
    } else {
      // Nanny
      steps.push(
        {
          element: '[data-testid="nav-rejoindre"]',
          popover: {
            title: 'Rejoindre un contrat',
            description: 'Entrez le code d\'invitation reçu de votre employeur pour vous lier à un contrat.',
            side: 'top',
          },
        },
        {
          element: '[data-testid="nav-mobile-profile-trigger"]',
          popover: {
            title: 'Plus — Mon profil',
            description: 'Accédez à votre profil, vos congés, et les paramètres de votre compte.',
            side: 'top',
          },
        },
      );
    }
  } else {
    // ── Desktop: top nav targets ──
    steps.push(
      {
        element: '[data-testid="nav-desktop-heures"]',
        popover: {
          title: 'Heures & Pointage',
          description: isParent
            ? 'Consultez et validez les heures de votre assistante maternelle. Les absences (congés payés, enfant malade…) apparaissent en couleur distincte.'
            : 'Enregistrez vos heures d\'arrivée et de départ. Déclarez vos absences (congés, maladie…). Votre employeur validera ensuite.',
          side: 'bottom',
        },
      },
      {
        element: '[data-testid="nav-desktop-contrats"]',
        popover: {
          title: 'Contrats',
          description: isParent
            ? 'Créez et gérez vos contrats (1 contrat = 1 enfant). Invitez votre nounou par email ou code. Consultez les détails et générez le PDF.'
            : 'Retrouvez tous vos contrats en cours. Consultez les détails (horaires, indemnités) et téléchargez le PDF.',
          side: 'bottom',
        },
      },
    );

    if (isParent) {
      steps.push(
        {
          element: '[data-testid="nav-desktop-paie"]',
          popover: {
            title: 'Paie & Pajemploi',
            description: 'Consultez les bulletins de paie mensuels avec les cotisations sociales officielles. Déclarez directement la rémunération à Pajemploi/URSSAF via l\'API intégrée.',
            side: 'bottom',
          },
        },
        {
          element: '[data-testid="user-menu-button"]',
          popover: {
            title: 'Mon profil & Paramètres',
            description: 'Accédez à votre profil, abonnement Premium (multi-enfants, paie automatique), cotisations sociales, congés, et RGPD.',
            side: 'left',
          },
        },
      );
    } else {
      // Nanny
      steps.push(
        {
          element: '[data-testid="nav-desktop-rejoindre"]',
          popover: {
            title: 'Rejoindre un contrat',
            description: 'Entrez le code d\'invitation reçu de votre employeur pour vous lier à un contrat.',
            side: 'bottom',
          },
        },
        {
          element: '[data-testid="user-menu-button"]',
          popover: {
            title: 'Mon profil',
            description: 'Accédez à votre profil, vos congés, et les paramètres de votre compte.',
            side: 'left',
          },
        },
      );
    }
  }

  // ── Stats cards (same for both) ──
  steps.push({
    element: '[data-testid="stat-total-hours"]',
    popover: {
      title: 'Statistiques mensuelles',
      description: 'Les cartes résument les données clés du mois sélectionné. Changez de mois avec le sélecteur.',
      side: isMobile ? 'bottom' : 'bottom',
    },
  });

  // ── Wrap-up (no element — centered) ──
  steps.push({
    popover: {
      title: 'C\'est parti ! 🎉',
      description: isParent
        ? 'Vous êtes prêt à utiliser Nannyou ! Commencez par créer un contrat, puis invitez votre assistante maternelle.'
        : 'Vous êtes prêt ! Si votre employeur ne vous a pas encore invité, demandez-lui le code ou le lien d\'invitation.',
    },
  });

  return steps;
};

export const GuidedTour = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const driverRef = useRef(null);
  const hasStartedRef = useRef(false);
  const [tourChecked, setTourChecked] = useState(false);
  const [shouldShowTour, setShouldShowTour] = useState(false);

  // ── Check if user needs tour ──
  useEffect(() => {
    if (!user || tourChecked) return;

    const checkTourStatus = async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        const completed = res.data?.has_completed_tour;
        setShouldShowTour(!completed);
      } catch {
        // If we can't check, don't show (safe default)
        setShouldShowTour(false);
      } finally {
        setTourChecked(true);
      }
    };

    checkTourStatus();
  }, [user, tourChecked]);

  // ── Listen for tour reset events (from Profile page) ──
  useEffect(() => {
    const handleReset = () => {
      setTourChecked(false);
      setShouldShowTour(false);
      hasStartedRef.current = false;
    };
    window.addEventListener('nannyou-reset-tour', handleReset);
    return () => window.removeEventListener('nannyou-reset-tour', handleReset);
  }, []);

  // ── Mark tour as completed (called on finish OR skip/close) ──
  const completeTour = useCallback(async () => {
    try {
      await axios.post(`${API}/profile/complete-tour`);
    } catch (err) {
      console.warn('[Tour] Failed to persist tour completion:', err.message);
    }
    setShouldShowTour(false);
  }, []);

  // ── Start the tour on the dashboard ──
  useEffect(() => {
    if (
      !shouldShowTour ||
      hasStartedRef.current ||
      !user ||
      location.pathname !== '/accueil'
    ) {
      return;
    }

    // Wait a tick for DOM elements to render
    const timer = setTimeout(() => {
      // Double-check we're still on dashboard and haven't started
      if (hasStartedRef.current) return;
      hasStartedRef.current = true;

      const isMobile = window.innerWidth < 768;
      const steps = buildSteps(user.role, isMobile);

      const d = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        animate: true,
        overlayColor: 'rgba(20, 30, 50, 0.6)',
        stagePadding: 8,
        stageRadius: 12,
        allowClose: true,
        disableActiveInteraction: true,
        popoverClass: 'nannyou-tour-popover',
        nextBtnText: 'Suivant →',
        prevBtnText: '← Précédent',
        doneBtnText: 'Terminer ✓',
        progressText: '{{current}} / {{total}}',
        steps,
        onCloseClick: () => {
          // User clicks X mid-tour => dismiss permanently
          d.destroy();
          completeTour();
        },
        onDestroyStarted: () => {
          // Called when tour ends (finish or destroy)
          if (!d.hasNextStep()) {
            d.destroy();
            completeTour();
          } else {
            d.destroy();
            completeTour();
          }
        },
      });

      driverRef.current = d;
      d.drive();
    }, 1200); // Allow dashboard to load data first

    return () => clearTimeout(timer);
  }, [shouldShowTour, user, location.pathname, completeTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch {}
      }
    };
  }, []);

  return null; // This component doesn't render any visible DOM
};
